import { useEffect, useState } from "react";
import { APK_DOWNLOAD_URL } from "@/lib/api";
import { Smartphone, Apple, Download, Check, Sparkles, Share, Monitor } from "lucide-react";

export default function InstallPage() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onBefore = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setDeferredPrompt(null);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="install-page">
      <header className="mb-10 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-[#FF0050] font-black">Metazik sur votre téléphone</p>
        <h1 className="text-4xl sm:text-5xl font-black font-display text-white mt-3">Installer l'app</h1>
        <p className="text-zinc-400 mt-3 max-w-xl mx-auto text-sm">
          Gratuit, sans carte bancaire. Choisissez votre appareil.
        </p>
      </header>

      <section className="grid md:grid-cols-2 gap-6">
        {/* ANDROID */}
        <div className="rounded-3xl p-7 border border-white/10 bg-gradient-to-br from-[#0A0A0A] via-zinc-950 to-[#1a0a14]" data-testid="install-android-card">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center">
              <Smartphone className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-black font-display text-white">Android</h2>
              <p className="text-xs text-zinc-400">Application native (fichier APK)</p>
            </div>
          </div>

          <a
            href={APK_DOWNLOAD_URL}
            rel="noopener noreferrer"
            className="mt-6 w-full py-4 rounded-2xl bg-gradient-to-r from-emerald-400 to-emerald-600 text-black font-black text-base flex items-center justify-center gap-2 hover:opacity-90 transition"
            data-testid="apk-download-btn"
          >
            <Download className="w-5 h-5" /> Télécharger pour Android
          </a>

          <ol className="mt-5 list-decimal list-inside space-y-1.5 text-zinc-400 leading-relaxed text-sm">
            <li>Touchez le bouton ci-dessus (≈ 70 Mo) depuis votre téléphone</li>
            <li>Ouvrez le fichier <span className="text-white font-semibold">Metazik.apk</span> téléchargé</li>
            <li>Si Android le demande, autorisez <span className="text-white font-semibold">« Installer des applications inconnues »</span> pour votre navigateur, puis revenez en arrière</li>
            <li>Touchez <span className="text-white font-semibold">Installer</span></li>
          </ol>

          {installed ? (
            <div className="mt-5 p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm flex items-center gap-2">
              <Check className="w-4 h-4" /> Metazik est installé sur votre écran d'accueil.
            </div>
          ) : deferredPrompt ? (
            <button
              onClick={handleInstall}
              className="mt-5 w-full py-3 rounded-2xl bg-white/10 border border-white/15 text-white font-bold text-sm hover:bg-white/15 transition"
              data-testid="install-android-btn"
            >
              Ou ajouter le site à l'écran d'accueil
            </button>
          ) : null}
        </div>

        {/* IPHONE */}
        <div className="rounded-3xl p-7 border border-white/10 bg-gradient-to-br from-[#0A0A0A] via-zinc-950 to-[#0a1a1a]" data-testid="install-ios-card">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-zinc-200 to-zinc-400 grid place-items-center">
              <Apple className="w-6 h-6 text-black" />
            </div>
            <div>
              <h2 className="text-2xl font-black font-display text-white">iPhone</h2>
              <p className="text-xs text-zinc-400">Depuis Safari, en 3 touches</p>
            </div>
          </div>

          <ol className="mt-6 list-decimal list-inside space-y-2 text-zinc-400 leading-relaxed text-sm">
            <li>Ouvrez <span className="text-[#00F2FE] break-all">{window.location.origin}</span> dans <span className="text-white font-semibold">Safari</span></li>
            <li>Touchez <Share className="w-4 h-4 inline text-white" /> <span className="text-white font-semibold">Partager</span> (en bas de l'écran)</li>
            <li>Touchez <span className="text-white font-semibold">« Sur l'écran d'accueil »</span>, puis <span className="text-white font-semibold">Ajouter</span></li>
          </ol>
          <p className="text-[11px] text-zinc-500 mt-4 leading-relaxed">
            L'icône Metazik apparaît comme une application. La version App Store arrivera plus tard.
          </p>
        </div>
      </section>

      <section className="mt-6 rounded-3xl p-6 border border-white/10 bg-white/[0.02] flex items-start gap-4" data-testid="install-desktop">
        <Monitor className="w-6 h-6 text-zinc-300 shrink-0 mt-0.5" />
        <div className="text-sm text-zinc-400 leading-relaxed">
          <p className="font-bold text-white">Sur ordinateur</p>
          <p className="mt-1">
            Dans Chrome ou Edge, cliquez sur l'icône d'installation à droite de la barre d'adresse (ou menu ⋮ → « Installer Metazik »). Le site s'ouvre alors dans sa propre fenêtre, comme une application.
          </p>
        </div>
      </section>

      <section className="mt-8 rounded-3xl p-7 border border-white/10 bg-white/[0.02] text-center" data-testid="install-cta">
        <Sparkles className="w-6 h-6 text-[#FFD60A] mx-auto" />
        <h3 className="text-lg font-black font-display text-white mt-2">Créez votre compte en 30 secondes</h3>
        <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">
          Une fois l'app installée, inscrivez-vous gratuitement en tant qu'artiste ou fan pour publier, écouter et soutenir.
        </p>
      </section>
    </div>
  );
}
